import { useEffect, useRef } from "react";
import * as THREE from "three";

export default function ChamberScene({ shells = 0, turn = false, recoil = 0 }: { shells?: number; turn?: boolean; recoil?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x07070a);
    scene.fog = new THREE.FogExp2(0x07070a, 0.055);

    const camera = new THREE.PerspectiveCamera(55, 1, 0.05, 50);
    camera.position.set(0, 2.15, 5.8);
    camera.lookAt(0, 1.15, -1.1);

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: "high-performance" });
    renderer.setPixelRatio(1);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.style.touchAction = "none";
    renderer.domElement.style.cursor = "grab";
    host.appendChild(renderer.domElement);

    // Seated look-around: clamped yaw/pitch driven by mouse drag or one-finger touch drag.
    // No free 360 spin and no walking — this only offsets where the fixed seat looks.
    const YAW_LIMIT = 0.6, PITCH_LIMIT = 0.32, SENSITIVITY = 0.0055;
    let yaw = 0, pitch = 0, dragging = false, lastX = 0, lastY = 0;
    const clamp = (v: number, lim: number) => Math.max(-lim, Math.min(lim, v));
    const onPointerDown = (e: PointerEvent) => {
      dragging = true; lastX = e.clientX; lastY = e.clientY;
      renderer.domElement.setPointerCapture(e.pointerId);
      renderer.domElement.style.cursor = "grabbing";
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return;
      const dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      yaw = clamp(yaw - dx * SENSITIVITY, YAW_LIMIT);
      pitch = clamp(pitch - dy * SENSITIVITY, PITCH_LIMIT);
    };
    const onPointerUp = (e: PointerEvent) => {
      dragging = false;
      try { renderer.domElement.releasePointerCapture(e.pointerId); } catch {}
      renderer.domElement.style.cursor = "grab";
    };
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("pointercancel", onPointerUp);
    renderer.domElement.addEventListener("pointerleave", onPointerUp);

    const resize = () => {
      const w = Math.max(1, host.clientWidth), h = Math.max(1, host.clientHeight);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(Math.max(320, Math.floor(w * 0.72)), Math.max(240, Math.floor(h * 0.72)), false);
      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";
      renderer.domElement.style.imageRendering = "pixelated";
    };
    const ro = new ResizeObserver(resize); ro.observe(host); resize();

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(30, 30), new THREE.MeshStandardMaterial({ color: 0x050506, roughness: 1 }));
    floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true; scene.add(floor);
    const back = new THREE.Mesh(new THREE.BoxGeometry(20, 8, 0.3), new THREE.MeshStandardMaterial({ color: 0x111014, roughness: 1 }));
    back.position.set(0, 3.2, -5); scene.add(back);

    const table = new THREE.Mesh(new THREE.CylinderGeometry(4.2, 4.35, 0.48, 32), new THREE.MeshStandardMaterial({ color: 0x20181a, roughness: 0.68 }));
    table.position.y = 0.72; table.castShadow = true; table.receiveShadow = true; scene.add(table);
    const edge = new THREE.Mesh(new THREE.TorusGeometry(4.0, 0.10, 8, 32), new THREE.MeshStandardMaterial({ color: 0x655055, metalness: 0.65, roughness: 0.32 }));
    edge.rotation.x = Math.PI / 2; edge.position.y = 0.97; scene.add(edge);

    const mat = (color: number, metal = 0, rough = 0.7) => new THREE.MeshStandardMaterial({ color, metalness: metal, roughness: rough });
    const gun = new THREE.Group();
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.105, 0.125, 2.7, 12), mat(0x45464a, 0.85, 0.3));
    barrel.rotation.z = Math.PI / 2; barrel.position.set(0.85, 1.22, 0.1); barrel.castShadow = true; gun.add(barrel);
    const barrel2 = barrel.clone(); barrel2.position.z = -0.1; gun.add(barrel2);
    const receiver = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.38, 0.52), mat(0x17181b, 0.9, 0.25));
    receiver.position.set(-0.25, 1.18, 0); receiver.castShadow = true; gun.add(receiver);
    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.95, 0.46), mat(0x161012, 0, 0.85));
    grip.position.set(-0.82, 0.76, 0); grip.rotation.z = -0.25; grip.castShadow = true; gun.add(grip);
    const trigger = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.035, 6, 12, Math.PI), mat(0x38383b, 0.7, 0.3));
    trigger.rotation.z = Math.PI / 2; trigger.position.set(-0.48, 1.03, 0); gun.add(trigger);
    gun.rotation.z = -0.055; scene.add(gun);

    // Low-poly dealer silhouette across the table.
    const dealer = new THREE.Group();
    const torso = new THREE.Mesh(new THREE.BoxGeometry(1.7, 2.1, 0.65), mat(0x141217, 0, 1)); torso.position.y = 2.0; dealer.add(torso);
    const head = new THREE.Mesh(new THREE.IcosahedronGeometry(0.48, 1), mat(0x201b20, 0, 0.95)); head.position.y = 3.35; dealer.add(head);
    const collar = new THREE.Mesh(new THREE.ConeGeometry(0.62, 0.55, 5), mat(0x272129)); collar.position.y = 2.8; dealer.add(collar);
    dealer.position.set(0, 0, -3.05); scene.add(dealer);

    const shellsGroup = new THREE.Group(); scene.add(shellsGroup);
    const shellMat = mat(0x8d7b73, 0.25, 0.45);
    const brass = mat(0x9a6d36, 0.7, 0.3);
    for (let i = 0; i < Math.min(shells, 8); i++) {
      const g = new THREE.Group();
      const body = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.34, 8), shellMat);
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.075, 0.035, 8), brass); base.position.y = -0.18; g.add(body, base);
      g.position.set(-2.1 + i * 0.58, 1.16, 0.92); g.rotation.z = Math.PI / 2; g.castShadow = true; shellsGroup.add(g);
    }

    // Small table props.
    for (let i = 0; i < 5; i++) {
      const card = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.035, 0.92), mat(i === 0 ? 0x312a2d : 0x18161a, 0, 0.9));
      card.position.set(-1.55 + i * 0.75, 1.02, 1.45 + (i % 2) * 0.15); card.rotation.y = (i - 2) * 0.08; card.castShadow = true; scene.add(card);
    }
    const ashtray = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.22, 0.08, 12), mat(0x3a3639, 0.6, 0.4)); ashtray.position.set(2.0, 1.05, 1.2); scene.add(ashtray);
    const glass = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.14, 0.48, 12, 1, true), new THREE.MeshPhysicalMaterial({ color: 0x4a4b50, transparent: true, opacity: 0.55, roughness: 0.25, transmission: 0.1 })); glass.position.set(-2.1, 1.25, 1.15); scene.add(glass);

    const hemi = new THREE.HemisphereLight(0x8b7b8f, 0x030304, 1.05); scene.add(hemi);
    const key = new THREE.SpotLight(0xe0b47e, 80, 16, Math.PI / 5, 0.7, 1.5); key.position.set(0, 5.6, 1); key.target.position.set(0, 0, -1); key.castShadow = true; scene.add(key, key.target);
    const rim = new THREE.PointLight(0x6c536d, 20, 10); rim.position.set(-4, 2.8, -3); scene.add(rim);

    let raf = 0;
    const start = performance.now();
    const animate = () => {
      const t = (performance.now() - start) / 1000;
      const kick = recoil * Math.max(0, 1 - ((t * 5) % 1));
      gun.position.z = kick * 0.18;
      gun.rotation.x = kick * 0.05;
      const focus = turn ? 0.02 : 0;
      camera.position.x = Math.sin(t * 0.35) * 0.018 + focus;
      camera.position.y = 2.15 + Math.sin(t * 0.7) * 0.008;
      camera.lookAt(0, 1.22, -1.15);
      camera.rotateY(yaw);
      camera.rotateX(pitch);
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf); ro.disconnect(); renderer.dispose();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointermove", onPointerMove);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("pointercancel", onPointerUp);
      renderer.domElement.removeEventListener("pointerleave", onPointerUp);
      scene.traverse(o => { const m = o as THREE.Mesh; if (m.geometry) m.geometry.dispose(); const material = m.material; if (Array.isArray(material)) material.forEach(x => x.dispose()); else if (material) material.dispose(); });
      renderer.domElement.remove();
    };
  }, [shells, turn, recoil]);
  return <div ref={ref} className="threeScene" aria-label="Night Chamber 3D game table" />;
}
